













































































































































































































































































import { VNode, CreateElement } from 'vue';
import { SwExtendedVue } from '../sw-extended-vue';
import { drag, DragPosition } from '../drag-dir';
import { Component, Prop, Model } from 'vue-property-decorator';
import { SwIcon } from '../sw-icon';
import { domService } from '../dom-service';
import { dropdown, SwDropdownUnit, DropdownDirValues } from '../dropdown-dir';
import { utilService } from '../util-service';
import { SliderCategoryOption } from './types';

const defaultThumbHeight = 23;

/**
 * @event change `(value: number | string | undefined, sourceOperation: SliderChangeSrcOperation)`
 *  the number value if not using categories,
 *  or string value if category array (string element or `id` from SliderCategoryOption),
 *  or `undefined` if no matched value at position.
 * @event drag-start `()`
 * @event drag-done `(value: number | string | undefined)` also emit the value like `change` event
 */
@Component({
  components: {
    SwIcon,
  },
  directives: {
    drag,
    dropdown,
  },
})
export default class SwSlider extends SwExtendedVue {
  /** could be `light` or `dark` */
  @Prop({ type: String, default: 'light' })
  theme: 'light' | 'dark';

  @Prop({ type: Number, default: 99 })
  max: number;

  @Prop({ type: Number, default: 0 })
  min: number;

  @Prop({ type: Number, default: 1 })
  step: number;

  @Prop({ type: Array })
  categories: (string | SliderCategoryOption)[];

  // value passed in may has meaning. eg: 14hours, min:1hr, max:24hr?
  @Model('change', {
    default: 1,
    type: [Number, String],
  })
  value: number|string;

  /** could be small, medium, large */
  @Prop({ type: String, default: 'small' })
  size: 'small' | 'medium' | 'large';

  @Prop(Number)
  circleSize: number;

  @Prop({ type: Boolean, default: false })
  disabled: boolean;

  @Prop({ type: String })
  label: string;

  @Prop({ type: Boolean, default: false })
  vertical: boolean;

  /** use dropdown to show available options, only valid for large-sized category slider */
  @Prop({ type: Boolean, default: false })
  withDropdown: boolean;

  cssPrefix: string = 'sw-slider';

  basebarSize: number = 1;

  sliderBarId = 'slider';
  sliderBtnId = 'sliderBtn';

  storedDragInfo: { rect: ClientRect, value: number } | null = null;
  dropdownVisible: boolean = false;

  get effectiveWithDropdown() {
    return this.hasCategory && this.size === 'large' && this.withDropdown;
  }

  get btnSizeMap(): {[size: string]: number} {
    return {
      small: this.circleSize || 11,
      medium: this.circleSize || 23,
      large: this.circleSize || 90,
    };
  }

  get numericValue(): number {
    if (!this.hasCategory) {
      return Math.max(this.min, Math.min(this.max, Number(this.value)));
    }
    const index = this.categories.findIndex((cat) => {
      if (typeof cat === 'string') {
        return cat === String(this.value);
      }
      if (typeof cat === 'object') {
        return cat.id === String(this.value);
      }
      return false;
    });
    return Math.max(0, index);
  }

  get hasCategory() {
    return Array.isArray(this.categories);
  }

  get hasLabel() {
    return !!this.label;
  }

  get max4Category() {
    if (this.hasCategory) return this.categories.length - 1;
    return this.max;
  }

  get valueRange(): number {
    const range = this.max4Category - this.min;
    if (range <= 0) { return 0.000001; }
    return range;
  }

  get disableDec() {
    if (this.disabled) { return true; }
    if (this.hasCategory && this.categories.length <= 0) { return true; }
    if (this.numericValue === this.min) return true;
    return false;
  }

  get disableInc() {
    if (this.disabled) { return true; }
    if (this.hasCategory && this.categories.length <= 0) { return true; }
    if (this.numericValue === this.max4Category) return true;
    return false;
  }

  get displayLabel() {
    // if not small thumb btn, display text.
    let visible = false;
    let disVal = this.getDisplayValue(this.numericValue);
    if (!this.isSmallBtn) {
      visible = true;
      if (this.size === 'large' && this.hasLabel) {
        disVal = `${this.label} ${disVal}`;
      }
    }
    return visible ? disVal : '';
  }

  get isCircleBtn() {
    return this.size !== 'large';
  }

  /** circleBtn backgroundColor/text depend on btnWidth, not on sliderType anymore. */
  get isSmallBtn() {
    return this.size === 'small';
  }

  /**
   * return slider thumb height, small/medium sized slider's circleSize is optional.
   */
  get mappedBtnHeight(): number {
    if (this.size === 'large' && !this.vertical) { return defaultThumbHeight; }
    return this.btnSizeMap[this.size];
  }

  get mappedBtnWidth(): number {
    if (this.size === 'large' && this.vertical) { return defaultThumbHeight; }
    return this.btnSizeMap[this.size];
  }

  get btnOffSet(): string {
    return `${(this.numericValue - this.min) / this.valueRange * (this.basebarSize - this.mappedBtnWidth)}px`;
  }

  get verticalBtnOffSet(): string {
    return `${(this.numericValue - this.min) / this.valueRange * (this.basebarSize - this.mappedBtnHeight)}px`;
  }

  get dropdownDirValue(): DropdownDirValues {
    if (!this.effectiveWithDropdown) { return {}; }
    return {
      placement: 'bottom-end',
      visible: this.dropdownVisible,
      withArrow: false,
      content: (h: CreateElement): VNode[] => {// tslint:disable-line
        const nodes: VNode[] = [];
        this.categories.forEach((cat, index) => {
          nodes.push(
            <SwDropdownUnit
              nativeOn-click={() => { this.onClickDropdownOption(cat); }}
            >
              <span class={[`${this.cssPrefix}__option-check-cont`, 'sw-flexbox__flex-none']}>
              {
                this.numericValue === index ? (
                  <SwIcon props-as-block props-name="checkmark" props-size={14} props-color={utilService.colorConst.selected} />
                ) : undefined
              }
              </span>
              <span class={['sw-flexbox__flex']}>{ typeof cat === 'string' ? cat : cat.label }</span>
            </SwDropdownUnit> as VNode,
          );
        });
        return nodes;
      },
      onClickOutsidePopper: () => { this.dropdownVisible = false; },
    };
  }

  updateBasebarSize() {
    const basebarEl = this.$refs[this.sliderBarId] as HTMLElement;
    if (!basebarEl) { return; }
    this.basebarSize = this.vertical ? basebarEl.offsetHeight : basebarEl.offsetWidth;
  }

  public increaseValue () {
    if (this.disabled) { return; }
    this.$emit('change', this.getEffectiveValue(Math.min(this.numericValue + this.step, this.max4Category)), 'step');
  }

  public decreaseValue () {
    if (this.disabled) { return; }
    this.$emit('change', this.getEffectiveValue(Math.max(this.numericValue - this.step, this.min)), 'step');
  }

  // calc the actual value of the range based on mousePosition & value range.
  calcSliderValue(clientPos: number) {
    if (!this.storedDragInfo) { return this.getEffectiveValue(this.numericValue); }

    let offset = this.vertical ? (clientPos - this.storedDragInfo.rect.top) : (clientPos - this.storedDragInfo.rect.left);
    const disPerValue = (this.basebarSize - (this.vertical ? this.mappedBtnHeight : this.mappedBtnWidth)) / this.valueRange;

    if (this.vertical) { offset = -1 * offset; }
    const newVal = this.validateValue(Math.round((offset / disPerValue) + this.storedDragInfo.value));
    return this.getEffectiveValue(newVal);
  }

  onDragStart() {
    if (this.disabled) { return; }
    this.storedDragInfo = { rect: (this.$refs[this.sliderBtnId] as HTMLElement).getBoundingClientRect(), value: this.numericValue };
    this.$emit('drag-start');
  }

  /** when calculating vertical slider value, use Y offset as input. */
  onPointerDrag(toClientPos: DragPosition) {
    if (this.disabled) { return; }
    const newVal = this.calcSliderValue(this.vertical ? toClientPos.y : toClientPos.x);
    if (this.value !== newVal) {
      this.$emit('change', newVal, 'drag');
    }
  }

  onDragDone(toClientPos: DragPosition) {
    if (this.disabled) { return; }
    this.storedDragInfo = null;
    const valWhenDone = this.calcSliderValue(this.vertical ? toClientPos.y : toClientPos.x);
    this.$emit('drag-done', valWhenDone);
  }

  private onClickDropdownTrigger() {
    this.dropdownVisible = !this.dropdownVisible;
  }

  private onClickDropdownOption(cat: string | SliderCategoryOption) {
    if (this.disabled) { return; }
    this.$emit('change', typeof cat === 'string' ? cat : cat.id, 'step');
    this.dropdownVisible = false;
  }

  /** transform slider num value to category data if it's category type. */
  getEffectiveValue(value: number) {
    if (this.hasCategory) {
      if (this.categories.length <= 0) { return undefined; }
      const data = this.categories[value] || this.categories[0];
      return (typeof data === 'string') ? data : data.id;
    }
    return value;
  }

  getDisplayValue(value: number) {
    if (this.hasCategory) {
      if (this.categories.length <= 0) { return ''; }
      const data = this.categories[value] || this.categories[0];
      return (typeof data === 'string') ? data : data.label;
    }
    return String(value);
  }

  validateValue(value: number) {
    if (value > this.max4Category) return this.max4Category;
    if (value < this.min) return this.min;
    return value;
  }

  mounted() {
    this.$nextTick(() => {
      this.updateBasebarSize();
      domService.resizeObserver.on(this.$refs[this.sliderBarId] as HTMLElement, this.updateBasebarSize);
    });
  }

  beforeDestroy() {
    domService.resizeObserver.off(this.$refs[this.sliderBarId] as HTMLElement, this.updateBasebarSize);
  }
}
